"use client";
import { motion } from "framer-motion";
import { BgLayer, Drift } from "./bg/BgPrimitives";

const steps = [
  {
    number: 1,
    title: "Register or log in",
    description:
      "Create your account, or sign in if you have already been invited by your school, university, or program coordinator.",
  },
  {
    number: 2,
    title: "Complete 8 self-paced online sessions",
    description:
      "Move through the sessions at your own pace and build your understanding step by step through practical learning and quizzes.",
  },
  {
    number: 3,
    title: "Join the final live activity",
    description:
      "Take part in a live activity in person or in hybrid format with a mentor or volunteer to bring your learning closer to real life.",
  },
  {
    number: 4,
    title: "Use support when needed",
    description:
      "Get help with the platform or with financial concepts through the built-in support tools.",
  },
  {
    number: 5,
    title: "Complete the program and earn your certificate",
    description:
      "Finish the learning journey and receive your certificate of completion.",
  },
];

// Per-step bg + number-text colors (navy on lighter shades, white on dark greens)
const stepBg = [
  "bg-journey-1 text-foreground",
  "bg-journey-2 text-foreground",
  "bg-journey-3 text-foreground",
  "bg-journey-4 text-white",
  "bg-journey-5 text-white",
];

export default function Journey() {
  return (
    <section id="how-to-start" className="relative py-16 md:py-32 bg-muted overflow-hidden">
      <div className="hidden md:block">
      <BgLayer>
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1200 600">
          {/* Road stroke → brand-navy (#22404d) */}
          <path d="M-50,300 C200,150 400,450 600,300 S1000,150 1250,300" stroke="var(--foreground)" strokeWidth="60" strokeLinecap="round" fill="none"/>
          <path d="M-50,300 C200,150 400,450 600,300 S1000,150 1250,300" stroke="#ffffff" strokeWidth="3" strokeDasharray="22 18" fill="none" opacity="0.85"/>
          {[[120,250],[350,380],[600,300],[850,220],[1080,340]].map(([x,y],i)=>(
            <g key={i}>
              <circle cx={x} cy={y} r="14" stroke="var(--foreground)" fill="none" opacity="0.25"/>
              <circle cx={x} cy={y} r="6" fill="var(--foreground)" opacity="0.55"/>
            </g>
          ))}
          {/* Arrow */}
          <g transform="translate(1080, 280)">
            <line x1="0" y1="0" x2="0" y2="-48" stroke="var(--foreground)" strokeWidth="2" opacity="0.7"/>
            <path d="M0,-46 L24,-38 L0,-30 Z" fill="var(--foreground)" opacity="0.85"/>
          </g>
        </svg>
        <Drift dx={6} dy={10} dur={15}>
          <div style={{ position: "absolute", left: "8%", top: "75%", border: "2.5px solid var(--foreground)", color: "var(--foreground)", padding: "6px 14px", borderRadius: "6px", fontWeight: 900, fontSize: "13px", letterSpacing: "0.18em", transform: "rotate(-10deg)", opacity: 0.85 }}>START</div>
        </Drift>
        <Drift dx={-8} dy={-6} dur={17} delay={1.5}>
          <div style={{ position: "absolute", left: "88%", top: "20%", border: "2.5px solid var(--foreground)", background: "var(--foreground)", color: "white", padding: "6px 14px", borderRadius: "6px", fontWeight: 900, fontSize: "13px", letterSpacing: "0.18em", transform: "rotate(14deg)", opacity: 0.85 }}>GO!</div>
        </Drift>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, var(--foreground) 1.2px, transparent 1.4px)", backgroundSize: "24px 24px", opacity: 0.10, WebkitMaskImage: "linear-gradient(to bottom, black, transparent 30%)", maskImage: "linear-gradient(to bottom, black, transparent 30%)" }}/>
      </BgLayer>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            How to start
          </h2>
          <p className="text-foreground/70 text-base md:text-lg">Getting started is simple.</p>
        </motion.div>

        {/* Desktop: alternating top/bottom along the road */}
        <div className="hidden md:grid grid-cols-5 gap-4 relative">
          {steps.map((step, i) => {
            const isTop = i % 2 === 0;
            return (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: isTop ? -20 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className={`flex flex-col items-center text-center ${isTop ? "" : "mt-36"}`}
              >
                {!isTop && (
                  <div className="bg-card/90 backdrop-blur-sm border border-border px-3 py-3 mb-3 shadow-sm"
                    style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}>
                    <p className="font-semibold text-foreground text-sm mb-1 leading-snug">
                      {step.title}
                    </p>
                    <p className="text-xs text-foreground/60 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                )}
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className={`w-12 h-12 rounded-full ${stepBg[i]} flex items-center justify-center font-bold text-lg shadow-lg shrink-0`}
                >
                  {step.number}
                </motion.div>
                {isTop && (
                  <div className="bg-card/90 backdrop-blur-sm border border-border px-3 py-3 mt-3 shadow-sm"
                    style={{ clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}>
                    <p className="font-semibold text-foreground text-sm mb-1 leading-snug">
                      {step.title}
                    </p>
                    <p className="text-xs text-foreground/60 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden relative">
          <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-foreground/15" />
          <div className="flex flex-col gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="relative flex gap-4 items-start"
              >
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className={`relative z-10 w-10 h-10 rounded-full ${stepBg[i]} flex items-center justify-center font-bold text-base shrink-0 shadow-md ring-4 ring-muted`}
                >
                  {step.number}
                </motion.div>
                <div className="flex-1 min-w-0 pt-1.5">
                  <p className="font-semibold text-foreground text-sm mb-1 leading-snug">
                    {step.title}
                  </p>
                  <p className="text-sm text-foreground/65 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
